import React, { useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useApp } from "@/state/AppContext";
import { api, apiErrorMessage, TOKEN_KEY } from "@/lib/api";
import { roleHome } from "@/lib/roleHome";
import { isAdminHost } from "@/lib/adminHost";

export default function GoogleCallback() {
  const { t, auth, setSession, refreshCart } = useApp();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const exchanged = useRef(false);

  useEffect(() => {
    if (exchanged.current) return;
    exchanged.current = true;
    const handoff = params.get("handoff");
    const error = params.get("error");

    // Idempotent: the handoff code is single-use, so a reload / back-navigation to this URL finds it
    // spent. If a session already exists the sign-in DID complete - carry on to the right home
    // instead of reporting a failure (and never spend a second code over a live session).
    const goHome = (a) => {
      if (a?.is_platform_admin) navigate("/platform", { replace: true });
      else navigate(roleHome[a?.active_role_type] || "/account", { replace: true });
    };
    if (auth || (!handoff && localStorage.getItem(TOKEN_KEY))) {
      goHome(auth);
      return;
    }
    if (error || !handoff) {
      toast.error(t("auth.googleFailed"));
      navigate("/login", { replace: true });
      return;
    }
    (async () => {
      try {
        const { data } = await api.post("/auth/google/session", { handoff }, { skipAuthPrompt: true });
        setSession(data);
        // The cart is a citizen concept; `/cart` is refused (404) on the admin host and there is no
        // cart to refresh there. Skip it so the admin plane fires no citizen API.
        if (!isAdminHost()) await refreshCart();
        // A platform admin lands on the console regardless of active role. On the admin host the
        // sign-in carries no city, so the server mints no `platform_admin` role profile and
        // `active_role_type` is `operator`/`null` — routing by `roleHome[active_role_type]` sent the
        // admin to `/account` (or `/city-desk`), which the AdminPortal funnels to `/platform`, where
        // the role check then bounced them back: a redirect loop that rendered a blank page. The
        // `is_platform_admin` flag is the real authority (it is what the server authorises on), so
        // route by it first.
        if (data.is_platform_admin) {
          toast.success(t("auth.success"));
          navigate("/platform", { replace: true });
          return;
        }
        // No `needs_phone` redirect: the number is asked for at the first action that needs it.
        // A Google account with no seat at all — typically someone who signed in with an email
        // that is not on their operator/owner identity. They are signed in but have no desk, so
        // say why instead of dropping them on a blank one: sign in by phone, then link Google.
        const noSeat =
          !data.active_role_type &&
          (data.role_profiles?.length ?? 0) === 0;
        if (noSeat) {
          toast.error(t("auth.googleNoSeat"));
        } else {
          toast.success(t("auth.success"));
        }
        navigate(roleHome[data.active_role_type] || "/account", { replace: true });
      } catch (e) {
        // A spent code with a live session is not a failure (see above).
        if (localStorage.getItem(TOKEN_KEY)) {
          goHome(auth);
          return;
        }
        toast.error(apiErrorMessage(e, t("auth.googleFailed")));
        navigate("/login", { replace: true });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="grid min-h-[50vh] place-items-center text-sm text-muted-foreground" data-testid="google-callback">
      {t("auth.googleFinishing")}
    </div>
  );
}
